import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const sectionLinks = [
  { label: "Home", sectionId: "home" },
  { label: "About", sectionId: "about" },
  { label: "Certificates", sectionId: "certificates" },
  { label: "Contact", sectionId: "contact" },
];

const MyNavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isHomePage = location.pathname === "/";
  const isProjectsPage = location.pathname.startsWith("/projects");

  useEffect(() => {
    if (!isHomePage) {
      return;
    }

    const updateActiveSection = () => {
      const navbarHeight = 64;
      const activationOffset = navbarHeight + 120;
      const currentScrollPosition =
        window.scrollY + activationOffset;

      let currentSection = "home";

      sectionLinks.forEach(({ sectionId }) => {
        const section = document.getElementById(sectionId);

        if (
          section &&
          section.offsetTop <= currentScrollPosition
        ) {
          currentSection = sectionId;
        }
      });

      const pageBottomReached =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 20;

      if (pageBottomReached) {
        currentSection = "contact";
      }

      setActiveSection(currentSection);
    };

    updateActiveSection();

    window.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });

    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener(
        "scroll",
        updateActiveSection
      );

      window.removeEventListener(
        "resize",
        updateActiveSection
      );
    };
  }, [isHomePage]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const handleSectionClick = (sectionId) => {
    setIsMenuOpen(false);
    setActiveSection(sectionId);

    if (isHomePage) {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      return;
    }

    navigate("/", {
      state: {
        scrollTo: sectionId,
      },
    });
  };

  const desktopSectionClasses = (sectionId) => {
    const isActive =
      isHomePage && activeSection === sectionId;

    return `
      relative rounded-lg px-4 py-2 text-sm font-medium
      transition duration-200
      ${
        isActive
          ? "bg-zinc-100 text-zinc-950"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
      }
    `;
  };

  const mobileSectionClasses = (sectionId) => {
    const isActive =
      isHomePage && activeSection === sectionId;

    return `
      block w-full rounded-lg px-4 py-3 text-left
      text-sm font-medium transition duration-200
      ${
        isActive
          ? "bg-zinc-950 text-white"
          : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950"
      }
    `;
  };

  return (
    <header className="sticky top-0 z-50 border-b border-zinc-200 bg-white/90 text-zinc-900 shadow-sm backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Portfolio name */}
        <button
          type="button"
          onClick={() => handleSectionClick("home")}
          className="text-base font-semibold tracking-tight text-zinc-950 transition hover:text-indigo-600 sm:text-lg"
        >
          Kacper Agatowski
        </button>

        {/* Desktop navigation */}
        <div className="hidden items-center gap-1 md:flex">
          {sectionLinks.map(({ label, sectionId }) => {
            const isActive =
              isHomePage && activeSection === sectionId;

            return (
              <button
                key={sectionId}
                type="button"
                onClick={() =>
                  handleSectionClick(sectionId)
                }
                className={desktopSectionClasses(sectionId)}
              >
                {label}

                {isActive && (
                  <span className="absolute inset-x-3 -bottom-[5px] h-0.5 rounded-full bg-indigo-600" />
                )}
              </button>
            );
          })}

          <Link
            to="/projects/home"
            className={`
              relative rounded-lg px-4 py-2 text-sm
              font-medium transition duration-200
              ${
                isProjectsPage
                  ? "bg-zinc-100 text-zinc-950"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"
              }
            `}
          >
            Projects

            {isProjectsPage && (
              <span className="absolute inset-x-3 -bottom-[5px] h-0.5 rounded-full bg-indigo-600" />
            )}
          </Link>
        </div>

        {/* Mobile menu */}
        <div className="relative md:hidden">
          <button
            type="button"
            aria-label={
              isMenuOpen
                ? "Close navigation menu"
                : "Open navigation menu"
            }
            aria-expanded={isMenuOpen}
            onClick={() =>
              setIsMenuOpen((previous) => !previous)
            }
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-300 bg-white text-zinc-700 transition hover:bg-zinc-100"
          >
            {isMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6L18 18"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>

          {isMenuOpen && (
            <div className="absolute right-0 top-12 z-[60] w-64 rounded-xl border border-zinc-200 bg-white p-2 shadow-xl">
              {sectionLinks.map(({ label, sectionId }) => (
                <button
                  key={sectionId}
                  type="button"
                  onClick={() =>
                    handleSectionClick(sectionId)
                  }
                  className={mobileSectionClasses(sectionId)}
                >
                  {label}
                </button>
              ))}

              <Link
                to="/projects/home"
                onClick={() => setIsMenuOpen(false)}
                className={`
                  block rounded-lg px-4 py-3 text-sm
                  font-medium transition duration-200
                  ${
                    isProjectsPage
                      ? "bg-zinc-950 text-white"
                      : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950"
                  }
                `}
              >
                Projects
              </Link>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default MyNavBar;
